import { FC, useState } from "react";
import {
  useISirklogMessagePublish,
  useISirkView,
} from "features/CenterContent/RoleContent/ISirk/useISirkData";
import {
  Box,
  DropsButton,
  Flex,
  Stack,
  Text,
  Textarea,
} from "shared/components/Spor";
import { useISirkLogMessages } from "features/CenterContent/RoleContent/ISirk/useISirkLogMessages";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { useTheme } from "styled-components";
import { CreateIncidentLogMessageSchema } from "features/CenterContent/RoleContent/ISirk/NewLogMessageModal";
import { format } from "date-fns";

type Props = {
  selectedDate: string;
  shiftId: string;
  operatorName?: string;
};

export const ISirkModuleForShifts: FC<Props> = ({
  selectedDate,
  shiftId,
  operatorName,
}) => {
  const theme = useTheme();
  const { data } = useISirkView(selectedDate);

  const [logMessage, setLogMessage] = useState("");

  // Extract ISirk from shift number
  const isirkFromShift = shiftId.slice(0, 3);

  const filterIsirkBasedOnShift = data?.incidentNotes
    ?.filter((note) => note.isirk.endsWith(isirkFromShift))
    .map((note) => note.isirk);

  const iSirk = filterIsirkBasedOnShift?.toString();

  const { data: isirkMessage } = useISirkLogMessages(iSirk!);

  const { postIncidentLog } = useISirklogMessagePublish();

  const predefinedText = `For ${shiftId} / ${operatorName} / ${format(
    new Date(selectedDate),
    "dd.MM.yy",
  )}: `;

  const inputMsg = "Skriv loggmelding til i-sirklogg";

  const [inputLabel, setInputLabel] = useState(inputMsg);

  const onSubmit = () => {
    const payload: CreateIncidentLogMessageSchema = {
      message: `${predefinedText}  ${logMessage}`,
      isirkId: iSirk!,
      incidentId: null,
      date: format(Date.now(), "yyyy-MM-dd"),
    };
    postIncidentLog(payload);
    setLogMessage("");
  };

  return (
    <>
      <Stack>
        <Text variant="sm" color={theme.colorTextSecondary}>
          {predefinedText}
        </Text>
      </Stack>
      <Box mt={1}>
        <Textarea
          pt={2}
          label={inputLabel}
          name="logmessage"
          value={logMessage}
          onFocus={() => {
            setInputLabel("");
          }}
          onChange={(e) => {
            setInputLabel("");
            setLogMessage(e.target.value);
          }}
          onBlur={() => {
            if (!logMessage) {
              setInputLabel(inputMsg);
            }
          }}
          aria-errormessage="logmessage-error-message"
          minHeight="10rem"
        />
      </Box>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <DropsButton
          m={1}
          variant="secondary"
          onClick={onSubmit}
          disabled={!logMessage}
        >
          Send logg
        </DropsButton>
      </div>

      <Stack gap="0" align="stretch">
        {isirkMessage?.incidentLogs
          ?.filter((log) => {
            const msg = log.message ?? "";
            return msg.includes(shiftId) && msg.includes(operatorName ?? "");
          })
          .map((log) => {
            const msg = log.message ?? "";

            const colonIndex = msg.indexOf(":");

            const firstLine =
              colonIndex !== -1 ? msg.slice(0, colonIndex).trim() : msg;
            const secondLine =
              colonIndex !== -1 ? msg.slice(colonIndex + 1).trim() : "";

            return (
              <ElementWrapper
                key={log.id}
                selected={false}
                backgroundColor={theme.colorBackgroundTertiary}
                dashedColor="tertiary"
                isHover={false}
              >
                <Flex
                  style={{
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <Text fontWeight={600} fontSize="xs">
                      {new Date(log.timestamp).toLocaleTimeString("nb-NO", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Text>
                    <Text color={theme.colorTextSecondary}>
                      ({format(new Date(log.timestamp), "dd.MM.yy")})
                    </Text>
                    <Text
                      color={theme.colorTextSecondary}
                      style={{ marginLeft: "auto" }}
                      fontSize="xs"
                    >
                      {log.sourceSystem.name}
                    </Text>
                  </div>

                  <Text color={theme.colorTextSecondary}>{firstLine}</Text>
                  <Text>{secondLine}</Text>
                </Flex>
              </ElementWrapper>
            );
          })}
      </Stack>
    </>
  );
};
